import React, { useState, useEffect, useContext } from 'react';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { ChatBubbleLeftRightIcon, CheckIcon, ArrowPathIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../lib/format';

interface Ticket {
  id: string;
  user_id?: string;
  user_name?: string;
  service_ref?: string;
  subject: string;
  content: string;
  status: string;
  created_at: string;
}

interface Reply {
  id: string;
  ticket_id: string;
  content: string;
  is_admin: boolean;
  author_role: string;
  is_private?: boolean;
  created_at: string;
}

export default function AdminSupport() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [replies, setReplies] = useState<{ [ticketId: string]: Reply[] }>({});
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(new Set());
  const [replyInputs, setReplyInputs] = useState<{ [ticketId: string]: string }>({});
  const [privateNotes, setPrivateNotes] = useState<{ [ticketId: string]: boolean }>({});
  const [sendingReply, setSendingReply] = useState<{ [ticketId: string]: boolean }>({});

  useEffect(() => {
    fetchTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/support/tickets`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to fetch tickets');
      const data = await res.json();
      setTickets(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async (ticketId: string) => {
    try {
      const res = await fetch(`${API_URL}/admin/support/tickets/${ticketId}/replies`, { headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to fetch replies');
      const data = await res.json();
      setReplies((prev) => ({ ...prev, [ticketId]: data || [] }));
    } catch (err: any) {
      console.error(err);
    }
  };

  const toggleExpand = (ticketId: string) => {
    setExpandedTickets((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
        if (!replies[ticketId]) fetchReplies(ticketId);
      }
      return next;
    });
  };

  const sendReply = async (ticketId: string) => {
    const content = (replyInputs[ticketId] || '').trim();
    if (!content) return;
    setSendingReply((prev) => ({ ...prev, [ticketId]: true }));
    try {
      const res = await fetch(`${API_URL}/admin/support/tickets/${ticketId}/replies`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, is_private: privateNotes[ticketId] || false }),
      });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Failed to send reply'); }
      setReplyInputs((prev) => ({ ...prev, [ticketId]: '' }));
      await fetchReplies(ticketId);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSendingReply((prev) => ({ ...prev, [ticketId]: false }));
    }
  };

  const handleClose = async (ticket: Ticket) => {
    try {
      const res = await fetch(`${API_URL}/admin/support/tickets/${ticket.id}/close`, { method: 'PUT', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to close ticket');
      await fetchTickets();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const handleReopen = async (ticket: Ticket) => {
    try {
      const res = await fetch(`${API_URL}/admin/support/tickets/${ticket.id}/reopen`, { method: 'PUT', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to reopen ticket');
      await fetchTickets();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const handleDelete = async (ticket: Ticket) => {
    const ok = await confirm({ title: 'Delete ticket', message: 'Delete this ticket permanently?', confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/admin/support/tickets/${ticket.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to delete ticket');
      await fetchTickets();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const filteredTickets = tickets.filter((t) => filter === 'all' || t.status === filter);
  const openCount = tickets.filter((t) => t.status === 'open').length;
  const closedCount = tickets.filter((t) => t.status === 'closed').length;

  if (loading) return (
    <div className="space-y-6">
      <PageHeader
        icon={<ChatBubbleLeftRightIcon />}
        title="Support"
        description="Manage all customer support tickets."
      />
      <div className="rounded-lg border border-border bg-card p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          icon={<ChatBubbleLeftRightIcon />}
          title="Support"
          description="Manage all customer support tickets."
        />

        <div className="grid grid-cols-3 gap-4">
          {[{ label: 'Total', value: tickets.length }, { label: 'Open', value: openCount }, { label: 'Closed', value: closedCount }].map(({ label, value }) => (
            <div key={label} className="bg-card shadow rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{value}</p>
              <p className="text-sm text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          {(['all', 'open', 'closed'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer ${filter === f ? 'bg-primary text-white' : 'bg-card border border-border text-foreground hover:bg-muted/40'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filteredTickets.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No tickets found.</div>
          ) : filteredTickets.map((ticket) => (
            <div key={ticket.id} className="bg-card shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 flex items-start justify-between cursor-pointer hover:bg-muted/40" onClick={() => toggleExpand(ticket.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-sm font-semibold text-foreground truncate">{ticket.subject}</h3>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${ticket.status === 'open' ? 'bg-success/10 text-success ring-success/20' : 'bg-muted text-muted-foreground ring-gray-500/20'}`}>{ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}</span>
                    {ticket.service_ref && <span className="text-xs text-muted-foreground">Ref: {ticket.service_ref}</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{ticket.user_name || ticket.user_id} · {formatDateTime(ticket.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {ticket.status === 'open' ? (
                    <button type="button" onClick={() => handleClose(ticket)} title="Close ticket" className="text-success hover:text-success cursor-pointer"><CheckIcon className="h-5 w-5" /></button>
                  ) : (
                    <button type="button" onClick={() => handleReopen(ticket)} title="Reopen ticket" className="text-primary hover:text-primary/80 cursor-pointer"><ArrowPathIcon className="h-5 w-5" /></button>
                  )}
                  <button type="button" onClick={() => handleDelete(ticket)} title="Delete ticket" className="text-red-500 hover:text-red-700 cursor-pointer">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                  <svg className={`h-5 w-5 text-muted-foreground transition-transform ${expandedTickets.has(ticket.id) ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {expandedTickets.has(ticket.id) && (
                <div className="border-t border-border px-6 py-4 space-y-4">
                  <div className="bg-muted rounded-lg p-4 max-w-xl">
                    <p className="text-xs font-medium text-muted-foreground mb-1">{ticket.user_name || 'Customer'}</p>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">{formatDateTime(ticket.created_at)}</p>
                  </div>

                  {(replies[ticket.id] || []).map((reply) => (
                    <div key={reply.id} className={reply.is_private ? '' : reply.is_admin ? 'flex justify-end' : ''}>
                      {reply.is_private ? (
                        <div className="bg-warning/10 border border-warning/30 rounded-lg p-4 max-w-2xl">
                          <div className="flex items-center gap-1.5 mb-1">
                            <LockClosedIcon className="h-3.5 w-3.5 text-amber-600" />
                            <p className="text-xs font-medium text-warning">Private Note</p>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{reply.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">{formatDateTime(reply.created_at)}</p>
                        </div>
                      ) : reply.is_admin ? (
                        <div className="bg-primary/10 rounded-lg p-4 max-w-xl">
                          <p className="text-xs font-medium text-primary mb-1">Support Agent</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{reply.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">{formatDateTime(reply.created_at)}</p>
                        </div>
                      ) : (
                        <div className="bg-muted rounded-lg p-4 max-w-xl">
                          <p className="text-xs font-medium text-muted-foreground mb-1">{ticket.user_name || 'Customer'}</p>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{reply.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">{formatDateTime(reply.created_at)}</p>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id={`private-${ticket.id}`} checked={privateNotes[ticket.id] || false} onChange={(e) => setPrivateNotes((prev) => ({ ...prev, [ticket.id]: e.target.checked }))}
                        className="rounded border-border text-amber-500 focus:ring-amber-400" />
                      <label htmlFor={`private-${ticket.id}`} className="flex items-center gap-1 text-sm text-muted-foreground cursor-pointer">
                        <LockClosedIcon className="h-3.5 w-3.5" /> Private note (not visible to customer)
                      </label>
                    </div>
                    <div className="flex gap-2 items-end">
                      <textarea rows={2} placeholder="Type a reply… (Enter to send, Shift+Enter for new line)"
                        value={replyInputs[ticket.id] || ''}
                        onChange={(e) => setReplyInputs((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(ticket.id); } }}
                        disabled={sendingReply[ticket.id]}
                        className={`flex-1 block rounded-md border shadow-sm px-3 py-2 text-sm focus-visible:ring-ring focus:border-primary resize-none disabled:bg-muted/30 ${privateNotes[ticket.id] ? 'border-amber-300 bg-warning/10' : 'border-border'}`}
                      />
                      <button type="button" onClick={() => sendReply(ticket.id)} disabled={sendingReply[ticket.id] || !replyInputs[ticket.id]?.trim()}
                        className="inline-flex items-center rounded-md border border-transparent px-3 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                        {sendingReply[ticket.id] ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
